import { api } from '@/global/services/api'
import { isEmpty, isObject } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'
import documentsHistoryHelper from '../documents-history-helper'
import { notify } from '@/global/services/notification/notificationBus'
import tabsHelper from '../tabs-helper'

const DOCUMENTS_HISTORY_DATA_KEY = 'handoverDocuments'
const DOCUMENT_TYPE = 'excavation_handover_report'
export default {

  mixins: [
    documentsHistoryHelper,
    tabsHelper
  ],

  data () {
    return {
      handoverHistoryVisible: false,
      handoverShowEditButtons: false,
      handoverEditState: false,
      handoverShowDocumentPreview: false,
      handoverDocument: null,
      handoverDataForGenerationVisible: true,
      handoverTitle: this.$t('road-maintenance/aggregator.excavation_handover_report'),
      handoverBaseTitle: this.$t('road-maintenance/aggregator.excavation_handover_report'),
      handoverValidationErrors: {},
      handoverDocumentsVisible: true,
      handoverDocumentHistoryLoaded: false,
      handoverRefreshDisabled: false,
      handoverDocumentLoaders: [],
      showEhTabLoader: null
    }
  },

  computed: {
    excavationHandoverConfig () {
      return {
        title: this.$t('road-maintenance/aggregator.excavation_handover_report'),
        visible: this.$route.query.category !== 'request_new',
        key: 'handover',
        validationErrors: this.handoverValidationErrors,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}/store`,
            usePatch: false,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`,
            method: 'get'
          }
        },
        edit: this.handoverEditState,
        editUpdate: (event) => this.onHandoverEditUpdate(event),
        dataFetched: (event) => this.onHandoverDataFetched(event),
        afterCancel: (event) => this.onHandoverDataFetched(event),
        afterSave: (event) => this.onHandoverDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.onHandoverModifyData(event),
        showLoader: this.showEhTabLoader,
        hiddenFormKeys: [
          'html',
          'documents',
          'files',
          'roads'
        ],
        tabContent: [
          // title with buttons
          {
            component: this.tabActionsComponent,
            title: this.handoverTitle,
            showBackButton: {
              onGoBack: () => this.goBack()
            },
            showEditButtons: this.handoverShowEditButtons,
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                visible: !this.handoverShowEditButtons,
                disabled: this.handoverRefreshDisabled,
                click: async (event) => {
                  const { fetchTabData } = event || {}
                  this.handoverRefreshDisabled = true

                  await fetchTabData?.()
                  await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

                  setTimeout(() => {
                    this.handoverRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              },
              {
                type: 'base',
                label: this.$t('road-maintenance/aggregator.upload_document'),
                prependIcon: 'mdi-upload',
                visible: !this.handoverShowEditButtons && !!this.permissions?.canImportDocument,
                click: (event) => this.onHandoverUploadDocument(event)
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.change_status'),
                prependIcon: 'mdi-swap-horizontal',
                visible: !this.handoverShowEditButtons && !!this.permissions?.canChangeStatus,
                click: (event) => this.onChangeStatus(event)
              },
              {
                type: 'dropdown',
                buttonType: 'base',
                label: this.$t('road-maintenance/aggregator.generate_document'),
                color: '#65AD45',
                class: 'generate-document-btn',
                visible: !this.handoverShowEditButtons && (!!this.permissions?.canCreate || !!this.permissions?.canCreateExcavationHandoverReport),
                menuContentClass: 'menu-class',
                component: this.documentsGenerationDropdownMenuComponent,
                hasHistoryData: this.logsData?.[DOCUMENTS_HISTORY_DATA_KEY]?.length,
                menuItemClick: (event) => this.handoverMenuItemClick(event)
              },
              {
                type: 'base',
                label: this.viewPdfBtnLabel,
                color: '#6e736d',
                visible: this.handoverShowEditButtons,
                class: 'generate-document-btn',
                loaderConfig: {
                  opacity: 0,
                  color: '#FFFFFFF'
                },
                showLoader: this.showPreviewLoader,
                click: (event) => this.previewDocument(event, DOCUMENT_TYPE, this.handoverBaseTitle)
              }
            ],
            onCancelTabSave: () => this.onHandoverCancelTab(),
            onSaveClick: (event) => this.onHandoverSave(event)
          },
          // Data for document generation
          {
            component: this.columnsContentComponent,
            title: this.$t('road-maintenance/aggregator.data_for_handover_generation'),
            visible: this.handoverDataForGenerationVisible,
            rows: [
              // meta data
              {
                type: 'fields',
                columnsCount: 3,
                fields: [
                  {
                    key: 'addresses',
                    label: this.$t('road-maintenance/aggregator.handover_road'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'city_district',
                    label: this.$t('road-maintenance/aggregator.city_district'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'date_time',
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.date,
                    placeholder: FORMATS.date,
                    label: this.$t('road-maintenance/excavation-service/tabs/handover/columns.handover_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          date_time: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate || !!this.permissions?.canCreateExcavationHandoverReport,
                    doubleClickEdit: false
                  },
                  {
                    key: 'excavation_reason',
                    label: this.$t('road-maintenance/aggregator.excavation_installation'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'consent_number',
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.consent_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'consent_date',
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.consent_date'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'repair_investor_name',
                    label: this.$t('road-maintenance/aggregator.investor_of_the_repair'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_name',
                    label: this.$t('road-maintenance/dropdown-types.contractor'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'consent_work_timeframe',
                    label: this.$t('road-maintenance/aggregator.consent_work_timeframe'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'repair_investor_responsible_person',
                    label: this.$t('road-maintenance/dropdown-types.repair_investor_responsible_person'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_responsible_person',
                    label: this.$t('road-maintenance/aggregator.contractor_responsible_person'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'expense_location',
                    label: this.$t('road-maintenance/aggregator.handover_expense_location'),
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate || !!this.permissions?.canCreateExcavationHandoverReport,
                    doubleClickEdit: false
                  },
                  {
                    key: 'road_administration',
                    label: this.$t('road-maintenance/aggregator.handover_road_administration'),
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate || !!this.permissions?.canCreateExcavationHandoverReport,
                    doubleClickEdit: false
                  },
                  {
                    key: 'repair_date',
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.date,
                    placeholder: FORMATS.date,
                    label: this.$t('road-maintenance/excavation-service/tabs/cleanup/columns.cleanup_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          repair_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate || !!this.permissions?.canCreateExcavationHandoverReport,
                    doubleClickEdit: false
                  }
                ]
              },
              // Handover damages
              {
                key: 'handover_damages',
                title: {
                  text: this.$t('road-maintenance/aggregator.anticipated_damage')
                },
                style: this.$vuetify.breakpoint.mobile
                  ? { marginTop: '25px' }
                  : {
                      border: '1px solid #E5E7EB',
                      borderRadius: '16px',
                      padding: '24px'
                    },
                visible: true,
                component: this.handoverDamagesComponent,
                disabled: !this.permissions?.canUpdateRoadDamages,
                dropdownValues: this.dropdownValues
              }
            ]
          },
          // documents history
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.log_history'),
            visible: this.handoverHistoryVisible,
            externalData: this.logsData,
            dataKey: DOCUMENTS_HISTORY_DATA_KEY,
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance.date') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              {
                key: 'status',
                title: this.$t('road-maintenance/excavation-service.status') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              {
                key: 'user',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance.user') + ':'
              },
              {
                key: 'document',
                title: this.$t('road-maintenance/aggregator.document') + ':',
                visible: (event) => {
                  return event?.blockValue !== '-'
                },
                loaderIds: this.handoverDocumentLoaders,
                valueClass: 'history-block-value',
                prependImage: {
                  src: '/img/icons/pdf-icon.svg',
                  style: {
                    marginRight: '4%'
                  }
                },
                contentWrapperStyle: {
                  display: 'flex',
                  alignItems: 'center'
                },
                customValue: (data) => {
                  return data?.document?.name ? data.document.name : '-'
                },
                click: (event) => this.onHandoverDownloadDocument(event)
              },
              {
                visible: (event) => {
                  return !!event?.rowData?.document_data_id
                },
                component: this.createFromVersionBtnComponent,
                class: 'create-from-version-btn-block',
                onClick: (event) => this.onHandoverReportCreateFromVersion(event)
              }
            ]
          },
          // handover documents preview
          {
            component: this.documentPreviewComponent,
            visible: this.handoverShowDocumentPreview,
            content: this.handoverDocument
          }
        ]
      }
    }
  },

  methods: {
    async onHandoverDownloadDocument (event) {
      const { id: documentId } = event?.rowData || {}
      // skip if download is already in progress
      if (this.handoverDocumentLoaders.includes(documentId)) {
        return
      }
      // show document loader
      if (documentId && !this.handoverDocumentLoaders.includes(documentId)) {
        this.handoverDocumentLoaders.push(documentId)
      }

      // download document
      await this.downloadDocument(event)
      // stop document loader
      if (this.handoverDocumentLoaders.includes(documentId)) {
        this.handoverDocumentLoaders = this.handoverDocumentLoaders.filter(activeLoaderId => activeLoaderId !== documentId)
      }
    },

    async onHandoverGenerateDocument (event, showLast = false) {
      try {
        const { formData } = event || {}
        const { id } = this?.$route?.params || {}
        this.showEhTabLoader = true

        // validate data
        if (!id) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Missing traffic regulation id')
          return
        }
        if (!isObject(formData)) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Form data is invalid', formData)
          return
        }

        // Create formData object for sending
        const dataToSend = new FormData()
        dataToSend.append('date_time', formData?.date_time ? dateService.formatCustom(formData.date_time, FORMATS.date, FORMATS.isoDate) : '')
        dataToSend.append('repair_date', formData?.repair_date ? dateService.formatCustom(formData.repair_date, FORMATS.date, FORMATS.isoDate) : '')
        dataToSend.append('expense_location', formData?.expense_location ?? '')
        dataToSend.append('road_administration', formData?.road_administration ?? '')
        // map roads to send only needed data
        if (formData?.roads?.length) {
          for (const roadIndex in formData.roads) {
            const road = formData.roads[roadIndex]
            const roadDamages = road?.damages
            // send calculated quantity that is inside road
            dataToSend.append(`roads[${roadIndex}][calculated_quantity]`, road?.calculated_quantity || '')
            // send road id
            dataToSend.append(`roads[${roadIndex}][id]`, road?.id || '')
            // map damages to send only its description
            if (roadDamages && isObject(roadDamages)) {
              for (const damageKey of Object.keys(roadDamages)) {
                const damageDescription = roadDamages[damageKey]?.description
                if (damageDescription) {
                  dataToSend.append(`roads[${roadIndex}][damages][${damageKey}][description]`, damageDescription)
                }
              }
            }
          }
        }
        dataToSend.append('show_last_version', showLast)

        // Get document html
        const { data } = await api('road-maintenance').post(`traffic-regulation/${id}/${DOCUMENT_TYPE}`, dataToSend)

        this.handoverDocument = data
        this.$set(this.chooseSolutionTypeDialogConfig, 'showDialog', false)
        this.handoverShowDocumentPreview = true
        this.handoverEditState = true
        this.handoverShowEditButtons = true
        this.handoverDataForGenerationVisible = false
        this.handoverTitle = this.$t('road-maintenance/aggregator.handover_document_overview')
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.handoverValidationErrors = errors && isObject(errors) ? errors : {}
        console.error(exception)
      }
      finally {
        this.showEhTabLoader = false
      }
    },

    async onHandoverReportCreateFromVersion (event) {
      try {
        this.showEhTabLoader = true

        // get document data
        const data = await this.createFromVersion(event, DOCUMENT_TYPE)

        const { html, css } = data || {}
        // do not open document if data is invalid
        if (!html && !css) {
          return
        }

        // set document data and open document preview
        this.handoverDocument = data
        this.$set(this.chooseSolutionTypeDialogConfig, 'showDialog', false)
        this.handoverShowDocumentPreview = true
        this.handoverEditState = true
        this.handoverShowEditButtons = true
        this.handoverDataForGenerationVisible = false
        this.handoverTitle = this.$t('road-maintenance/aggregator.handover_document_overview')
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showEhTabLoader = false
      }
    },

    onHandoverCancelTab () {
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle.data_not_saved_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.handoverCancel())
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    handoverCancel () {
      this.handoverEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
    },

    onHandoverSave (event) {
      const { saveTab } = event || {}
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('road-maintenance/aggregator.document_will_be_uploaded_to_centrix_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onHandoverSaveConfirmation(saveTab))
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    onHandoverSaveConfirmation (saveTabFn) {
      if (typeof saveTabFn === 'function') {
        saveTabFn()
      }

      this.handoverEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
      this.$set(this.informationDialogConfig, 'title', this.informationDialogBaseConfig.title)
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', this.informationDialogBaseConfig.confirmCallbackFunction)
    },

    onHandoverEditUpdate (event) {
      const { edit } = event || {}
      this.handoverHistoryVisible = !edit

      if (!edit) {
        this.handoverShowDocumentPreview = false
        this.handoverEditState = false
        this.handoverShowEditButtons = false
        this.handoverDataForGenerationVisible = true
        this.handoverDocument = null
        this.handoverTitle = this.handoverBaseTitle
      }
    },

    async onHandoverDataFetched (event, afterSave = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      const data = apiData?.data || formDataForSend || {}

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          date_time: data?.date_time ? dateService.formatCustom(data.date_time, FORMATS.isoDate, FORMATS.date) : null,
          repair_date: data?.repair_date ? dateService.formatCustom(data.repair_date, FORMATS.isoDate, FORMATS.date) : null
        })
      }

      if (!this.handoverDocumentHistoryLoaded || afterSave) {
        this.handoverDocumentHistoryLoaded = true
        const { id: suggestedStatusId, description } = data?.suggested_status || {}

        // Fetch documents logs
        await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

        // Open change status popup after documents save only if we have suggested status provided
        if (suggestedStatusId) {
          this.$set(this.afterDocumentStatusChangeActions, 'statusId', suggestedStatusId)
          const message = this.$t('road-maintenance/aggregator.status_change_message')
          this.$set(this.informationDialogConfig, 'showDialog', afterSave)
          this.$set(this.informationDialogConfig, 'message', '<div> ' + message + '<span style="font-style: italic; color: green;">' + ' ' + this.$t(description) + '</span>?</div>')
        }
      }
    },

    onHandoverModifyData (event) {
      const { copiedData } = event || {}

      if (copiedData) {
        copiedData.date_time = copiedData?.date_time ? dateService.formatCustom(copiedData.date_time, FORMATS.date, FORMATS.isoDate) : null
        copiedData.repair_date = copiedData?.repair_date ? dateService.formatCustom(copiedData.repair_date, FORMATS.date, FORMATS.isoDate) : null

        if (copiedData?.roads?.length) {
          for (const roadIndex in copiedData.roads) {
            const road = copiedData.roads[roadIndex]
            const roadDamages = road?.damages
            // map damages to send only its description
            if (roadDamages && isObject(roadDamages)) {
              for (const damageKey of Object.keys(roadDamages)) {
                const concreteDamageObj = roadDamages[damageKey]
                if (isEmpty(concreteDamageObj)) {
                  delete roadDamages[damageKey]
                }
                else {
                  // send only description
                  roadDamages[damageKey] = {
                    description: concreteDamageObj.description || ''
                  }
                }
              }
            }
          }
        }
      }

      return copiedData
    },

    async handoverMenuItemClick (event) {
      const { menuIndex } = event || {}
      await this.onHandoverGenerateDocument(event, menuIndex === 1)
    },

    onHandoverUploadDocument (event) {
      const { fetchTabData } = event || {}
      this.$set(this.uploadSignedDocumentDialogConfig, 'showDialog', true)
      if (typeof fetchTabData === 'function') {
        this.$set(this.uploadSignedDocumentDialogConfig.config, 'documentKey', DOCUMENT_TYPE)
        this.$set(this.uploadSignedDocumentDialogConfig.config, 'fetchTabData', async () => {
          fetchTabData()
          await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)
        })
      }
    }
  }
}
